/**
 * Employer early-access application types (tabbio.com/employers).
 *
 * The const arrays here are the single source of truth for the allowed values,
 * shared across the boundary for the same reason the partner ones are: apps/web
 * is on zod ^4 and apps/api is on zod ^3, so the Zod schemas are duplicated on
 * each side while both build their `z.enum(...)` from these arrays.
 *
 * Deliberately a separate family from PARTNER_*, not a variant of it. A partner
 * application and an employer application share a shape but not a lifecycle: the
 * partner table carries a global one-application-per-address unique index, so
 * folding employers into it would stop a recruiter who already applied to the
 * partner program from ever asking for hiring access.
 */

/**
 * Why they want in. Multi-select and optional: the form accepts an application
 * with none of these ticked, because a name and a company is already a lead
 * worth reading, and a required question here would only teach people to pick
 * something at random.
 *
 * Both real reasons can be selected together, which is how "both" is expressed:
 * there is no third combined option to keep out of sync with the two atoms.
 * `other` is the escape hatch and carries `otherDetails` with it.
 */
export const EMPLOYER_REASONS = [
  'find_candidates',
  'post_jobs',
  'other',
] as const;

export type EmployerReason = (typeof EMPLOYER_REASONS)[number];

/** Same five states the partner queue uses, declared separately so the two triage flows can diverge without a coordinated edit. */
export const EMPLOYER_APPLICATION_STATUSES = [
  'new',
  'contacted',
  'approved',
  'rejected',
  'spam',
] as const;

export type EmployerApplicationStatus =
  (typeof EMPLOYER_APPLICATION_STATUSES)[number];

/** Delivery state of the applicant acknowledgement email. */
export const EMPLOYER_ACK_EMAIL_STATUSES = [
  'pending',
  'sent',
  'failed',
] as const;

export type EmployerAckEmailStatus =
  (typeof EMPLOYER_ACK_EMAIL_STATUSES)[number];

export interface SubmitEmployerApplicationInput {
  /** The applicant's name, as they typed it. */
  fullName: string;
  /** Work address. Validated as an email, not as a corporate domain: plenty of real Gulf SMEs hire from a Gmail account. */
  email: string;
  companyName: string;
  /** Careers page, site, or LinkedIn. Optional. */
  companyWebsite?: string | null;
  /** Empty array is a valid answer; the question is optional. */
  reasons?: EmployerReason[];
  /** Free text behind the `other` reason. Optional even when `other` is ticked. */
  otherDetails?: string | null;
  /** The exact localized consent sentence the applicant saw on screen. */
  consentText?: string | null;
  locale?: 'en' | 'ar';
  sourcePath?: string | null;
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  /** Off-screen bot trap; real users leave it empty. */
  companyWebsiteConfirm?: string | null;
}

export interface SubmitEmployerApplicationResult {
  received: true;
}

/** Admin-facing row shape. Provenance fields stay server side. */
export interface EmployerApplicationSummary {
  id: string;
  fullName: string;
  email: string;
  companyName: string;
  companyWebsite: string | null;
  reasons: EmployerReason[];
  otherDetails: string | null;
  status: EmployerApplicationStatus;
  ackEmailStatus: EmployerAckEmailStatus;
  locale: string;
  sourcePath: string | null;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  countryCode: string | null;
  adminNotes: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}
